// Razorpay rewards carry two image fields and they are not equally reliable:
//
//   display_parameters.image_url — a presigned assets-v2.poshvine.com link. Every
//     one of these currently answers 403 AccessDenied; the signature baked into
//     the catalogue response is stale, and stripping the query does not help
//     (the bare object is 403 too). So it must never be tried first.
//   brand.logo_url — a plain, unsigned assets.poshvine.com asset. Serves 200
//     reliably, but roughly half the catalogue leaves it empty.
//
// Ordering logo_url first means the common case paints on the first request
// instead of after a failed round-trip, while still picking up image_url if
// Razorpay ever starts signing those links correctly.
// Assets that serve 200 but paint nothing, so no onError can catch them. Listed
// here so they fall through to the lettered tile instead of an empty box.
//   decathlon-logo.svg — ships two viewBox attributes; the one the browser honours
//     ("71.32 28.78 45.17 45.17") frames a region the artwork never reaches, so the
//     SVG renders blank. Drop this entry if the provider ever fixes the asset.
import { resolveUpload } from './uploads';

const BLANK_ASSETS = new Set([
  'https://assets.poshvine.com/brands/decathlon/decathlon-logo.svg',
]);

/**
 * As of the 2026-08-06 backend the server probes every candidate once, caches
 * the verdict and publishes only URLs that actually serve — so the reliability
 * ordering below is no longer ours to guess. Prefer that list.
 *
 * `display_image_url: null` is a *positive* answer, not a missing field: it
 * means every candidate is known dead, so callers should go straight to the
 * lettered tile rather than pay for a request that will 403. Distinguishing
 * that from "older response shape" is why this checks for the array rather
 * than truthiness of the URL.
 *
 * BLANK_ASSETS still applies on top: the backend probes for HTTP status, and
 * the Decathlon SVG answers 200 while rendering nothing.
 */
export const getRewardImageCandidates = (reward) => {
  if (!reward) return [];

  /*
    Admin artwork first, and resolved.

    Two separate things were wrong the day admin uploads shipped. The API stores
    an upload as a relative path (`/uploads/gc_x.png`) — correct, since an
    absolute URL in the database breaks when the API moves host — but an <img>
    given that raw resolves it against the page's own origin and 404s in silence.
    And an override is only an override if it is tried before the provider's
    links, whose presigned image_url 403s for every card in the catalogue.

    Doing it here covers the admin table, the grid, the product page and the cart
    at once, because every reward image in the app comes through this function.
  */
  const candidates = Array.isArray(reward.display_image_candidates)
    ? reward.display_image_candidates
    // Fallback for a cached or pre-2026-08-06 response.
    : [reward.brand?.logo_url, reward.display_parameters?.image_url];

  return [reward.admin_image_url, ...candidates]
    .filter((url) => url && !BLANK_ASSETS.has(url))
    .map(resolveUpload)
    .filter((url, i, all) => all.indexOf(url) === i);
};

/**
 * True when the backend has confirmed there is no working image anywhere
 * upstream. 71 of the 276 live rewards are in this state (Khadims, Me N Moms,
 * Liberty Stores, …) and it is not a frontend bug — render the lettered tile
 * and make no request.
 */
export const hasNoKnownImage = (reward) =>
  Array.isArray(reward?.display_image_candidates) && reward.display_image_candidates.length === 0;

// The catalogue's display name is often a verbose SKU ("Apple Premium Reseller
// Voucher 6M"); the brand name is the one customers recognise.
export const getRewardName = (reward) =>
  reward?.brand?.name || reward?.display_parameters?.name || reward?.name || '';
